import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const SOUND_STORAGE_KEY = 'dios10_sounds_enabled';

const SoundContext = createContext();

export const useSound = () => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};

/** Hook per wrappare onClick con suono - uso: onClick={wrapWithSound(handleClick)} o onClick={wrapWithSound(handleClick, 'success')} */
export const useSoundableClick = (defaultType = 'click') => {
  const { playSound } = useSound();
  return (handler, soundType = defaultType) => (e) => {
    playSound(soundType);
    handler?.(e);
  };
};

// Web Audio API - genera suoni senza file esterni
const createSound = (audioContext, type) => {
  const now = audioContext.currentTime;
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  switch (type) {
    case 'click': {
      // Breve "tap" secco - perfetto per pulsanti
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, now);
      oscillator.frequency.exponentialRampToValueAtTime(400, now + 0.05);
      gainNode.gain.setValueAtTime(0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
      oscillator.start(now);
      oscillator.stop(now + 0.08);
      break;
    }
    case 'success': {
      // Tono ascendente - azione completata
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(523, now);
      oscillator.frequency.setValueAtTime(659, now + 0.1);
      oscillator.frequency.setValueAtTime(784, now + 0.2);
      gainNode.gain.setValueAtTime(0.12, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      oscillator.start(now);
      oscillator.stop(now + 0.3);
      break;
    }
    case 'error': {
      // Tono discendente - errore
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(400, now);
      oscillator.frequency.exponentialRampToValueAtTime(150, now + 0.15);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      oscillator.start(now);
      oscillator.stop(now + 0.2);
      break;
    }
    case 'cardReveal': {
      // Suono "magico" quando si rivela una carta
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(440, now);
      oscillator.frequency.linearRampToValueAtTime(880, now + 0.2);
      oscillator.frequency.linearRampToValueAtTime(660, now + 0.35);
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.12, now + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      oscillator.start(now);
      oscillator.stop(now + 0.4);
      break;
    }
    case 'packOpen': {
      // Suono celebrativo apertura pack
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(392, now);
      oscillator.frequency.setValueAtTime(523, now + 0.1);
      oscillator.frequency.setValueAtTime(659, now + 0.2);
      oscillator.frequency.setValueAtTime(784, now + 0.3);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.linearRampToValueAtTime(0.15, now + 0.15);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
      oscillator.start(now);
      oscillator.stop(now + 0.5);
      break;
    }
    case 'hover': {
      // Suono molto leggero per hover (opzionale)
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(600, now);
      gainNode.gain.setValueAtTime(0.04, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.03);
      oscillator.start(now);
      oscillator.stop(now + 0.03);
      break;
    }
    default:
      return createSound(audioContext, 'click');
  }
};

export const SoundProvider = ({ children }) => {
  const [enabled, setEnabledState] = useState(() => {
    try {
      const stored = localStorage.getItem(SOUND_STORAGE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const audioContextRef = useRef(null);

  useEffect(() => {
    const initContext = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      return audioContextRef.current;
    };

    // Unlock Audio Context on first user interaction (browser policy)
    const handleFirstInteraction = () => {
      initContext();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });
    document.addEventListener('keydown', handleFirstInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  const setEnabled = useCallback((value) => {
    setEnabledState(value);
    try {
      localStorage.setItem(SOUND_STORAGE_KEY, String(value));
    } catch (e) {
      console.warn('Could not save sound preference:', e);
    }
  }, []);

  const playSound = useCallback((type = 'click') => {
    if (!enabled) return;

    try {
      const ctx = audioContextRef.current;
      if (!ctx) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;

      // Resume se sospeso (policy browser)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      createSound(audioContext, type);
    } catch (e) {
      console.warn('Sound playback failed:', e);
    }
  }, [enabled]);

  const value = {
    enabled,
    setEnabled,
    playSound,
    soundTypes: ['click', 'success', 'error', 'cardReveal', 'packOpen', 'hover']
  };

  return (
    <SoundContext.Provider value={value}>
      {children}
    </SoundContext.Provider>
  );
};
